/**
 * The curated SC2 command + roster DATA: the multiplayer/campaign commands and rosters hand-authored
 * here, merged with the generated co-op tables (sc2-coop.ts) and the campaign tables (sc2-campaign.ts).
 * Co-op ids are prefixed `co_`; reuse keeps shared ids (bRax, tStim, ...) single.
 *
 * Ported from the design prototype's `app/src/data.js`. Two deliberate deviations: the prototype's
 * macro trio (Idle, Town, Army) is omitted (the [Hotkeys] flatten already renders those ids
 * identically), and its "illustrative" notes are dropped (they caveat prototype sample data, not the
 * game). The SC2Command / SC2Roster types and the selector functions live in sc2-registry.
 */
import { CAMPAIGN_COMMANDS, CAMPAIGN_ROSTERS } from "./sc2-campaign";
import { COOP_COMMANDS, COOP_ROSTERS } from "./sc2-coop";
import type { SC2Command, SC2Roster } from "./sc2-registry";

const MP_COMMANDS: Record<string, SC2Command> = {
  // commands
  move: { name: "Move", category: "command", key: "Move" },
  attack: { name: "Attack", category: "command", key: "Attack" },
  stop: { name: "Stop", category: "command", key: "Stop" },
  hold: { name: "Hold Position", category: "command", key: "MoveHoldPosition" },
  patrol: { name: "Patrol", category: "command", key: "MovePatrol" },
  // Cancel is a global on every card; the unit-specific cancel
  // family (CancelCocoon, ...) stays uncurated and folds out
  cancel: { name: "Cancel", category: "command", key: "Cancel" },
  halt: { name: "Halt", category: "command", key: "Halt", unit: "SCV" },
  repair: { name: "Repair", category: "command", key: "Repair" },
  // universal worker commands on the worker card (verified vs the in-game General cards):
  // Return Cargo and Spray on every worker; Gather only on the Probe (GatherProt is the only
  // bound gather - the SCV/Drone use the default right-click, which carries no hotkey).
  returnCargo: { name: "Return Cargo", category: "command", key: "ReturnCargo" },
  spray: { name: "Spray", category: "command", key: "Spray" },
  gatherProt: { name: "Gather", category: "command", key: "GatherProt" },
  // build-menu openers: pressing one drills into its submenu of structures, which
  // live a menu level down. The same grid keys (K,P / Slash) carry the opener here and a
  // structure inside the submenu - never the same card, so never a collision.
  bBasic: {
    name: "Build Basic",
    category: "command",
    key: "TerranBuild",
    unit: "SCV",
    opens: "scvBasic",
  },
  bAdvanced: {
    name: "Build Advanced",
    category: "command",
    key: "TerranBuildAdvanced",
    unit: "SCV",
    opens: "scvAdvanced",
  },
  burrow: { name: "Burrow", category: "ability", key: "BurrowDown" },
  unburrow: { name: "Unburrow", category: "ability", key: "BurrowUp" },
  // Terran abilities
  // card-less legacy entry; the carded forms are tStim (the ability, key Stim) and the
  // tStimpack*TechLab/Reactor research entries. Its unit-less form is NOT a universal claim -
  // the game's Stimpack research is caster-scoped (Stimpack/BarracksTechLab).
  stim: { name: "Stimpack", short: "Stim", category: "ability", key: "Stimpack" },
  snipe: { name: "Snipe", category: "ability", key: "Snipe", unit: "Ghost" },
  emp: { name: "EMP", category: "ability", key: "EMP", unit: "Ghost" },
  holdfire: { name: "Hold Fire", category: "ability", key: "GhostHoldFire", unit: "Ghost" },
  nuke: { name: "Nuke", category: "ability", key: "NukeCalldown", unit: "Ghost" },
  heal: { name: "Heal", category: "ability", key: "MedicHeal", unit: "Medic" },
  // Terran build (SCV) - Build Basic submenu structures
  bCmdCtr: {
    name: "Command Center",
    short: "Cmd Ctr",
    category: "structure",
    key: "CommandCenter",
    unit: "SCV",
  },
  bDepot: { name: "Supply Depot", category: "structure", key: "SupplyDepot", unit: "SCV" },
  bRax: { name: "Barracks", category: "structure", key: "Barracks", unit: "SCV" },
  bRef: { name: "Refinery", category: "structure", key: "Refinery", unit: "SCV" },
  bEbay: {
    name: "Engineering Bay",
    short: "E-Bay",
    category: "structure",
    key: "EngineeringBay",
    unit: "SCV",
  },
  bBunker: { name: "Bunker", category: "structure", key: "Bunker", unit: "SCV" },
  bMissile: {
    name: "Missile Turret",
    short: "Turret",
    category: "structure",
    key: "MissileTurret",
    unit: "SCV",
  },
  bSensor: { name: "Sensor Tower", category: "structure", key: "SensorTower", unit: "SCV" },
  // Terran build (SCV) - Build Advanced submenu structures
  bFactory: { name: "Factory", category: "structure", key: "Factory", unit: "SCV" },
  bGhostAcad: { name: "Ghost Academy", category: "structure", key: "GhostAcademy", unit: "SCV" },
  bArmory: { name: "Armory", category: "structure", key: "Armory", unit: "SCV" },
  bStarport: { name: "Starport", category: "structure", key: "Starport", unit: "SCV" },
  bFusion: { name: "Fusion Core", category: "structure", key: "FusionCore", unit: "SCV" },
  // Terran train
  tMarine: { name: "Marine", category: "unit", key: "Marine", unit: "Barracks" },
  tMaraud: { name: "Marauder", category: "unit", key: "Marauder", unit: "Barracks" },
  tReaper: { name: "Reaper", category: "unit", key: "Reaper", unit: "Barracks" },
  tGhost: { name: "Ghost", category: "unit", key: "Ghost", unit: "Barracks" },
  aReactor: { name: "Reactor", category: "structure", key: "Reactor", unit: "Barracks" },
  tScv: { name: "SCV", category: "unit", key: "SCV" },
  mOrbital: {
    name: "Orbital Command",
    short: "Orbital",
    category: "structure",
    key: "OrbitalCommand",
    unit: "CommandCenter",
  },
  // Lift and Land are a state toggle on one key (grounded shows Lift, flying shows Land). We omit the
  // flying-building cards, so Land rides along as an overload note on Lift.
  lift: { name: "Lift Off", category: "command", key: "Lift", note: "Also Land (when flying)" },
  // Zerg morph (Larva)
  zDrone: { name: "Drone", category: "unit", key: "Drone", unit: "Larva" },
  // card-less legacy entry (the icon map keys off it); the carded form is zZerglingLarva. Its
  // unit-less form is NOT a universal claim - the game trains Zergling per caster (Zergling/Larva).
  zZergling: { name: "Zergling", category: "unit", key: "Zergling" },
  zRoach: { name: "Roach", category: "unit", key: "Roach", unit: "Larva" },
  zHydra: { name: "Hydralisk", category: "unit", key: "Hydralisk", unit: "Larva" },
  zMuta: { name: "Mutalisk", category: "unit", key: "Mutalisk", unit: "Larva" },
  zOver: { name: "Overlord", category: "unit", key: "Overlord", unit: "Larva" },
  zCorrupt: { name: "Corruptor", category: "unit", key: "Corruptor", unit: "Larva" },
  zInfest: { name: "Infestor", category: "unit", key: "Infestor", unit: "Larva" },
  // Zerg Queen
  qCreep: { name: "Creep Tumor", category: "ability", key: "BuildCreepTumor", unit: "Queen" },
  qTunnel: { name: "Deep Tunnel", category: "ability", key: "DeepTunnel", unit: "Queen" },
  // Zerg build (Drone) - openers, then the Build Basic / Build Advanced submenu structures
  zBuild: {
    name: "Build Basic",
    category: "command",
    key: "ZergBuild",
    unit: "Drone",
    opens: "droneBasic",
  },
  zBuildAdv: {
    name: "Build Advanced",
    category: "command",
    key: "ZergBuildAdvanced",
    unit: "Drone",
    opens: "droneAdvanced",
  },
  zbHatch: { name: "Hatchery", category: "structure", key: "Hatchery", unit: "Drone" },
  zbPool: { name: "Spawning Pool", category: "structure", key: "SpawningPool", unit: "Drone" },
  zbExtract: { name: "Extractor", category: "structure", key: "Extractor", unit: "Drone" },
  zbEvo: {
    name: "Evolution Chamber",
    short: "Evo",
    category: "structure",
    key: "EvolutionChamber",
    unit: "Drone",
  },
  zbRoachW: { name: "Roach Warren", category: "structure", key: "RoachWarren", unit: "Drone" },
  zbBane: { name: "Baneling Nest", category: "structure", key: "BanelingNest", unit: "Drone" },
  zbSpine: { name: "Spine Crawler", category: "structure", key: "SpineCrawler", unit: "Drone" },
  zbSpore: { name: "Spore Crawler", category: "structure", key: "SporeCrawler", unit: "Drone" },
  zbHydra: { name: "Hydralisk Den", category: "structure", key: "HydraliskDen", unit: "Drone" },
  zbInfPit: {
    name: "Infestation Pit",
    short: "InfPit",
    category: "structure",
    key: "InfestationPit",
    unit: "Drone",
  },
  zbSpire: { name: "Spire", category: "structure", key: "Spire", unit: "Drone" },
  zbNydus: { name: "Nydus Network", category: "structure", key: "NydusNetwork", unit: "Drone" },
  zbUltra: {
    name: "Ultralisk Cavern",
    short: "Ultra",
    category: "structure",
    key: "UltraliskCavern",
    unit: "Drone",
  },
  zbLurker: { name: "Lurker", category: "structure", key: "MutateintoLurkerDen", unit: "Drone" },
  // Protoss build (Probe) - openers, then the Build Basic / Build Advanced submenu structures
  pBuild: {
    name: "Build Basic",
    category: "command",
    key: "ProtossBuild",
    unit: "Probe",
    opens: "probeBasic",
  },
  pBuildAdv: {
    name: "Build Advanced",
    category: "command",
    key: "ProtossBuildAdvanced",
    unit: "Probe",
    opens: "probeAdvanced",
  },
  pNexus: { name: "Nexus", category: "structure", key: "Nexus", unit: "Probe" },
  pPylon: { name: "Pylon", category: "structure", key: "Pylon", unit: "Probe" },
  pGate: { name: "Gateway", category: "structure", key: "Gateway", unit: "Probe" },
  pAssim: { name: "Assimilator", category: "structure", key: "Assimilator", unit: "Probe" },
  pCyber: {
    name: "Cybernetics Core",
    short: "Cyber",
    category: "structure",
    key: "CyberneticsCore",
    unit: "Probe",
  },
  pForge: { name: "Forge", category: "structure", key: "Forge", unit: "Probe" },
  pCannon: { name: "Photon Cannon", category: "structure", key: "PhotonCannon", unit: "Probe" },
  pBattery: {
    name: "Shield Battery",
    short: "Battery",
    category: "structure",
    key: "ShieldBattery",
    unit: "Probe",
  },
  pTwilight: {
    name: "Twilight Council",
    short: "Twilight",
    category: "structure",
    key: "TwilightCouncil",
    unit: "Probe",
  },
  pStargate: { name: "Stargate", category: "structure", key: "Stargate", unit: "Probe" },
  pRobo: {
    name: "Robotics Facility",
    short: "Robo",
    category: "structure",
    key: "RoboticsFacility",
    unit: "Probe",
  },
  pArchives: {
    name: "Templar Archives",
    short: "Archives",
    category: "structure",
    key: "TemplarArchive",
    unit: "Probe",
  },
  pDark: { name: "Dark Shrine", category: "structure", key: "DarkShrine", unit: "Probe" },
  pFleet: { name: "Fleet Beacon", category: "structure", key: "FleetBeacon", unit: "Probe" },
  pRoboBay: { name: "Robotics Bay", category: "structure", key: "RoboticsBay", unit: "Probe" },
  // Protoss train (Gateway / Nexus)
  pZealot: { name: "Zealot", category: "unit", key: "Zealot" },
  pStalker: { name: "Stalker", category: "unit", key: "Stalker" },
  pSentry: { name: "Sentry", category: "unit", key: "Sentry" },
  pHT: { name: "High Templar", category: "unit", key: "HighTemplar" },
  pDT: { name: "Dark Templar", category: "unit", key: "DarkTemplar" },
  pBlink: { name: "Blink", category: "ability", key: "Blink", unit: "Stalker" },
  pProbe: { name: "Probe", category: "unit", key: "Probe", unit: "Nexus" },
  pMother: { name: "Mothership", category: "unit", key: "Mothership", unit: "Nexus" },
  pChrono: { name: "Chrono", category: "ability", key: "ChronoBoostEnergyCost", unit: "Nexus" },
  tStim: { name: "Stimpack", short: "Stim", category: "ability", key: "Stim" },
  tKD8ChargeReaper: { name: "KD8 Charge", category: "ability", key: "KD8Charge", unit: "Reaper" },
  tChannelSnipeGhost: {
    name: "Steady Targeting",
    category: "ability",
    key: "ChannelSnipe",
    unit: "Ghost",
  },
  // The Ghost's "Weapons Free" is a real, separate command-card button (TheCore binds WeaponsFree/Ghost
  // to its own key, distinct from GhostHoldFire) — the resume-fire counterpart to Hold Fire, kept.
  tWeaponsFreeGhost: {
    name: "Weapons Free",
    category: "ability",
    key: "WeaponsFree",
    unit: "Ghost",
  },
  tCloakOnBanshee: { name: "Cloak", category: "ability", key: "CloakOnBanshee" },
  tCloakOff: { name: "Decloak", category: "ability", key: "CloakOff" },
  tMorphToHellion: {
    name: "Hellion Mode",
    category: "ability",
    key: "MorphToHellion",
    unit: "Hellion",
  },
  tMorphToHellionTank: {
    name: "Hellbat Mode",
    category: "ability",
    key: "MorphToHellionTank",
    unit: "Hellion",
  },
  tWidowMineBurrow: {
    name: "Activate Mine",
    category: "ability",
    key: "WidowMineBurrow",
    unit: "WidowMine",
  },
  tWidowMineUnburrow: {
    name: "Deactivate Mine",
    category: "ability",
    key: "WidowMineUnburrow",
    unit: "WidowMine",
  },
  tLockOnCyclone: { name: "Lock On", category: "ability", key: "LockOn", unit: "Cyclone" },
  tSiegeMode: { name: "Siege Mode", category: "ability", key: "SiegeMode" },
  tUnsiege: { name: "Tank Mode", category: "ability", key: "Unsiege" },
  tArmorpiercingMode: {
    name: "High Impact Payload",
    category: "ability",
    key: "ArmorpiercingMode",
  },
  tExplosiveMode: { name: "Explosive Payload", category: "ability", key: "ExplosiveMode" },
  tAssaultMode: { name: "Assault Mode", category: "ability", key: "AssaultMode" },
  tFighterMode: { name: "Fighter Mode", category: "ability", key: "FighterMode" },
  tHealMedivac: { name: "Heal", category: "ability", key: "Heal", unit: "Medivac" },
  tMedivacSpeedBoost: {
    name: "Ignite Afterburners",
    category: "ability",
    key: "MedivacSpeedBoost",
    unit: "Medivac",
  },
  tBunkerLoad: { name: "Load", category: "command", key: "BunkerLoad" },
  tBunkerUnloadAll: { name: "Unload All", category: "command", key: "BunkerUnloadAll" },
  tLiberatorAGMode: {
    name: "Defender Mode",
    category: "ability",
    key: "LiberatorAGMode",
    unit: "Liberator",
  },
  tLiberatorAAMode: {
    name: "Fighter Mode",
    category: "ability",
    key: "LiberatorAAMode",
    unit: "Liberator",
  },
  tAutoTurretRaven: {
    name: "Auto-Turret",
    category: "structure",
    key: "AutoTurret",
    unit: "Raven",
  },
  tRavenScramblerMissile: {
    name: "Interference Matrix",
    category: "ability",
    key: "RavenScramblerMissile",
    unit: "Raven",
  },
  tRavenShredderMissile: {
    name: "Anti-Armor Missile",
    category: "ability",
    key: "RavenShredderMissile",
    unit: "Raven",
  },
  tYamatoGun: { name: "Yamato Cannon", category: "ability", key: "YamatoGun" },
  tHyperjumpBattlecruiser: {
    name: "Tactical Jump",
    category: "ability",
    key: "Hyperjump",
    unit: "Battlecruiser",
  },
  tUpgradeToPlanetaryFortressCom: {
    name: "Planetary Fortress",
    category: "command",
    key: "UpgradeToPlanetaryFortress",
    unit: "CommandCenter",
  },
  tSelectBuilder: { name: "Select Builder", category: "command", key: "SelectBuilder" },
  tRally: { name: "Set Rally Point", category: "command", key: "Rally" },
  tCommandCenterLoad: { name: "Load", category: "command", key: "CommandCenterLoad" },
  tCommandCenterUnloadAll: {
    name: "Unload All",
    category: "command",
    key: "CommandCenterUnloadAll",
  },
  tCalldownMULEOrbitalCommand: {
    name: "MULE",
    category: "ability",
    key: "CalldownMULE",
    unit: "OrbitalCommand",
  },
  tSupplyDropOrbitalCommand: {
    name: "Extra Supplies",
    category: "ability",
    key: "SupplyDrop",
    unit: "OrbitalCommand",
  },
  tScanOrbitalCommand: {
    name: "Scanner Sweep",
    category: "ability",
    key: "Scan",
    unit: "OrbitalCommand",
  },
  tStopPlanetaryFortress: {
    name: "Stop",
    category: "command",
    key: "StopPlanetaryFortress",
    unit: "PlanetaryFortress",
  },
  tHalt: { name: "Halt", category: "ability", key: "Halt" },
  tTechLabBarracks: {
    name: "Tech Lab",
    category: "structure",
    key: "TechLabBarracks",
    unit: "Barracks",
  },
  tResearchShieldWallBarracksTec: {
    name: "Combat Shield",
    category: "command",
    key: "ResearchShieldWall",
    unit: "BarracksTechLab",
  },
  tStimpackBarracksTechLab: {
    name: "Stimpack",
    category: "command",
    key: "Stimpack",
    unit: "BarracksTechLab",
  },
  tResearchPunisherGrenadesBarra: {
    name: "Concussive Shells",
    category: "command",
    key: "ResearchPunisherGrenades",
    unit: "BarracksTechLab",
  },
  tTerranInfantryWeaponsLevel1En: {
    name: "Infantry Weapons",
    category: "command",
    key: "TerranInfantryWeaponsLevel1",
    unit: "EngineeringBay",
  },
  tTerranInfantryArmorLevel1Engi: {
    name: "Infantry Armor",
    category: "command",
    key: "TerranInfantryArmorLevel1",
    unit: "EngineeringBay",
  },
  tResearchHiSecAutoTrackingEngi: {
    name: "Hi-Sec Auto Tracking",
    category: "command",
    key: "ResearchHiSecAutoTracking",
    unit: "EngineeringBay",
  },
  tUpgradeBuildingArmorLevel1Eng: {
    name: "Neosteel Armor",
    category: "command",
    key: "UpgradeBuildingArmorLevel1",
    unit: "EngineeringBay",
  },
  tSalvageBunker: { name: "Salvage", category: "command", key: "Salvage", unit: "Bunker" },
  tResearchPersonalCloakingGhost: {
    name: "Personal Cloaking",
    category: "command",
    key: "ResearchPersonalCloaking",
    unit: "GhostAcademy",
  },
  tNukeArmGhostAcademy: {
    name: "Arm Silo with Nuke",
    category: "ability",
    key: "NukeArm",
    unit: "GhostAcademy",
  },
  tHellionFactory: { name: "Hellion", category: "structure", key: "Hellion", unit: "Factory" },
  tWidowMineFactory: {
    name: "Widow Mine",
    category: "structure",
    key: "WidowMine",
    unit: "Factory",
  },
  tBuildCycloneFactory: {
    name: "Cyclone",
    category: "structure",
    key: "BuildCyclone",
    unit: "Factory",
  },
  tSiegeTankFactory: {
    name: "Siege Tank",
    category: "structure",
    key: "SiegeTank",
    unit: "Factory",
  },
  tHellionTankFactory: {
    name: "Hellbat",
    category: "structure",
    key: "HellionTank",
    unit: "Factory",
  },
  tThorFactory: { name: "Thor", category: "structure", key: "Thor", unit: "Factory" },
  tTechLabFactory: {
    name: "Tech Lab",
    category: "structure",
    key: "TechLabFactory",
    unit: "Factory",
  },
  tReactorFactory: { name: "Reactor", category: "structure", key: "Reactor", unit: "Factory" },
  tResearchHighCapacityBarrelsFa: {
    name: "Infernal Pre-Igniter",
    category: "command",
    key: "ResearchHighCapacityBarrels",
    unit: "FactoryTechLab",
  },
  tCycloneResearchLockOnDamageUp: {
    name: "Mag-Field Accelerator",
    category: "command",
    key: "CycloneResearchLockOnDamageUpgrade",
    unit: "FactoryTechLab",
  },
  tResearchDrillClawsFactoryTech: {
    name: "Drilling Claws",
    category: "command",
    key: "ResearchDrillClaws",
    unit: "FactoryTechLab",
  },
  tResearchSmartServosFactoryTec: {
    name: "Smart Servos",
    category: "command",
    key: "ResearchSmartServos",
    unit: "FactoryTechLab",
  },
  tTerranVehicleWeaponsLevel1Arm: {
    name: "Vehicle Weapons",
    category: "command",
    key: "TerranVehicleWeaponsLevel1",
    unit: "Armory",
  },
  tTerranVehicleAndShipPlatingLe: {
    name: "Vehicle and Ship Plating",
    category: "command",
    key: "TerranVehicleAndShipPlatingLevel1",
    unit: "Armory",
  },
  tTerranShipWeaponsLevel1Armory: {
    name: "Ship Weapons",
    category: "command",
    key: "TerranShipWeaponsLevel1",
    unit: "Armory",
  },
  tVikingFighterStarport: {
    name: "Viking",
    category: "structure",
    key: "VikingFighter",
    unit: "Starport",
  },
  tMedivacStarport: { name: "Medivac", category: "structure", key: "Medivac", unit: "Starport" },
  tLiberatorStarport: {
    name: "Liberator",
    category: "structure",
    key: "Liberator",
    unit: "Starport",
  },
  tRavenStarport: { name: "Raven", category: "structure", key: "Raven", unit: "Starport" },
  tBansheeStarport: { name: "Banshee", category: "structure", key: "Banshee", unit: "Starport" },
  tBattlecruiserStarport: {
    name: "Battlecruiser",
    category: "structure",
    key: "Battlecruiser",
    unit: "Starport",
  },
  tTechLabStarport: {
    name: "Tech Lab",
    category: "structure",
    key: "TechLabStarport",
    unit: "Starport",
  },
  tReactorStarport: { name: "Reactor", category: "structure", key: "Reactor", unit: "Starport" },
  tResearchBansheeCloakStarportT: {
    name: "Cloaking Field",
    category: "command",
    key: "ResearchBansheeCloak",
    unit: "StarportTechLab",
  },
  tBansheeSpeedStarportTechLab: {
    name: "Hyperflight Rotors",
    category: "command",
    key: "BansheeSpeed",
    unit: "StarportTechLab",
  },
  tResearchRavenInterferenceMatr: {
    name: "Interference Matrix",
    category: "command",
    key: "ResearchRavenInterferenceMatrix",
    unit: "StarportTechLab",
  },
  tResearchBattlecruiserSpeciali: {
    name: "Weapon Refit",
    category: "command",
    key: "ResearchBattlecruiserSpecializations",
    unit: "FusionCore",
  },
  tResearchMedivacEnergyUpgradeF: {
    name: "Caduceus Reactor",
    category: "command",
    key: "ResearchMedivacEnergyUpgrade",
    unit: "FusionCore",
  },
  tResearchBallisticRangeFusionC: {
    name: "Advanced Ballistics",
    category: "command",
    key: "ResearchBallisticRange",
    unit: "FusionCore",
  },
  zZerglingLarva: { name: "Zergling", category: "unit", key: "Zergling", unit: "Larva" },
  zSwarmHostMPLarva: { name: "Swarm Host", category: "unit", key: "SwarmHostMP", unit: "Larva" },
  zViperLarva: { name: "Viper", category: "unit", key: "Viper", unit: "Larva" },
  zUltraliskLarva: { name: "Ultralisk", category: "unit", key: "Ultralisk", unit: "Larva" },
  zMorphMorphaliskQueen: {
    name: "Spawn Larvae",
    category: "ability",
    key: "MorphMorphalisk",
    unit: "Queen",
  },
  zTransfusionQueen: {
    name: "Transfusion",
    category: "ability",
    key: "Transfusion",
    unit: "Queen",
  },
  zBuildCreepTumorPropagateCreep: {
    name: "Spawn Creep Tumor",
    category: "ability",
    key: "BuildCreepTumorPropagate",
    unit: "CreepTumorBurrowed",
  },
  zMorphToOverseerOverlord: {
    name: "Overseer",
    category: "unit",
    key: "MorphToOverseer",
    unit: "Overlord",
  },
  zGenerateCreepOverlord: {
    name: "Generate Creep",
    category: "ability",
    key: "GenerateCreep",
    unit: "Overlord",
  },
  zMorphtoOverlordTransport: {
    name: "Mutate Ventral Sacs",
    category: "ability",
    key: "MorphtoOverlordTransport",
    unit: "Overlord",
  },
  zStopGenerateCreep: {
    name: "Stop Generate Creep",
    category: "command",
    key: "StopGenerateCreep",
  },
  zMorphtoOverseerSiege: {
    name: "Oversight",
    category: "ability",
    key: "MorphtoOverseerSiege",
    unit: "Overseer",
  },
  zMorphtoOverseerNormal: {
    name: "Cancel Oversight",
    category: "command",
    key: "MorphtoOverseerNormal",
    unit: "Overseer",
  },
  zSpawnChangelingOverseer: {
    name: "Spawn Changeling",
    category: "ability",
    key: "SpawnChangeling",
    unit: "Overseer",
  },
  zContaminateOverseer: {
    name: "Contaminate",
    category: "ability",
    key: "Contaminate",
    unit: "Overseer",
  },
  zBanelingZergling: { name: "Baneling", category: "unit", key: "Baneling", unit: "Zergling" },
  zExplodeBaneling: { name: "Explode", category: "ability", key: "Explode", unit: "Baneling" },
  zEnableBuildingAttackBaneling: {
    name: "Enable Structure Attack",
    category: "ability",
    key: "EnableBuildingAttack",
    unit: "Baneling",
  },
  zDisableBuildingAttackBaneling: {
    name: "Disable Structure Attack",
    category: "ability",
    key: "DisableBuildingAttack",
    unit: "Baneling",
  },
  zRavagerRoach: { name: "Ravager", category: "unit", key: "Ravager", unit: "Roach" },
  zRavagerCorrosiveBile: {
    name: "Corrosive Bile",
    category: "ability",
    key: "RavagerCorrosiveBile",
    unit: "Ravager",
  },
  zHydraliskFrenzy: {
    name: "Lunge",
    category: "ability",
    key: "HydraliskFrenzy",
    unit: "Hydralisk",
  },
  zLurkerMPHydralisk: { name: "Lurker", category: "unit", key: "LurkerMP", unit: "Hydralisk" },
  zBurrowLurkerMP: { name: "Burrow", category: "ability", key: "BurrowLurkerMP" },
  zCausticSprayCorruptor: {
    name: "Caustic Spray",
    category: "ability",
    key: "CausticSpray",
    unit: "Corruptor",
  },
  zBroodLordCorruptor: {
    name: "Brood Lord",
    category: "unit",
    key: "BroodLord",
    unit: "Corruptor",
  },
  zAmorphousArmorcloudInfestor: {
    name: "Microbial Shroud",
    category: "ability",
    key: "AmorphousArmorcloud",
    unit: "Infestor",
  },
  zFungalGrowthInfestor: {
    name: "Fungal Growth",
    category: "ability",
    key: "FungalGrowth",
    unit: "Infestor",
  },
  zNeuralParasiteInfestor: {
    name: "Neural Parasite",
    category: "ability",
    key: "NeuralParasite",
    unit: "Infestor",
  },
  zVoidSwarmHostSpawnLocustSwarm: {
    name: "Spawn Locusts",
    category: "ability",
    key: "VoidSwarmHostSpawnLocust",
    unit: "SwarmHostMP",
  },
  zSwarmHostBurrowDown: { name: "Burrow", category: "ability", key: "SwarmHostBurrowDown" },
  zLocustMPFlyingSwoop: {
    name: "Swoop",
    category: "ability",
    key: "LocustMPFlyingSwoop",
    unit: "LocustMPFlying",
  },
  zViperConsume: { name: "Consume", category: "ability", key: "ViperConsume", unit: "Viper" },
  zFaceEmbraceViper: { name: "Abduct", category: "ability", key: "FaceEmbrace", unit: "Viper" },
  zBlindingCloudViper: {
    name: "Blinding Cloud",
    category: "ability",
    key: "BlindingCloud",
    unit: "Viper",
  },
  zParasiticBombViper: {
    name: "Parasitic Bomb",
    category: "ability",
    key: "ParasiticBomb",
    unit: "Viper",
  },
  zLarva: { name: "Select Larvae", category: "ability", key: "Larva" },
  zQueen: { name: "Birth Queen", category: "ability", key: "Queen" },
  zResearchBurrow: { name: "Burrow", category: "ability", key: "ResearchBurrow" },
  zOverlordspeed: { name: "Pneumatized Carapace", category: "ability", key: "overlordspeed" },
  zRallyEgg: { name: "Set Worker Rally Point", category: "ability", key: "RallyEgg" },
  zLairHatchery: { name: "Lair", category: "ability", key: "Lair", unit: "Hatchery" },
  zHiveLair: { name: "Hive", category: "ability", key: "Hive", unit: "Lair" },
  zZerglingmovementspeedSpawning: {
    name: "Metabolic Boost",
    category: "ability",
    key: "zerglingmovementspeed",
    unit: "SpawningPool",
  },
  zZerglingattackspeedSpawningPo: {
    name: "Adrenal Glands",
    category: "ability",
    key: "zerglingattackspeed",
    unit: "SpawningPool",
  },
  zZergmeleeweapons1EvolutionCha: {
    name: "Melee Attacks",
    category: "ability",
    key: "zergmeleeweapons1",
    unit: "EvolutionChamber",
  },
  zZergmissileweapons1EvolutionC: {
    name: "Missile Attacks",
    category: "ability",
    key: "zergmissileweapons1",
    unit: "EvolutionChamber",
  },
  zZerggroundarmor1EvolutionCham: {
    name: "Ground Carapace",
    category: "ability",
    key: "zerggroundarmor1",
    unit: "EvolutionChamber",
  },
  zEvolveGlialRegenerationRoachW: {
    name: "Glial Reconstitution",
    category: "ability",
    key: "EvolveGlialRegeneration",
    unit: "RoachWarren",
  },
  zEvolveTunnelingClawsRoachWarr: {
    name: "Tunneling Claws",
    category: "ability",
    key: "EvolveTunnelingClaws",
    unit: "RoachWarren",
  },
  zEvolveCentrificalHooksBanelin: {
    name: "Centrifugal Hooks",
    category: "ability",
    key: "EvolveCentrificalHooks",
    unit: "BanelingNest",
  },
  zSpineCrawlerRootSpineCrawlerU: {
    name: "Root",
    category: "ability",
    key: "SpineCrawlerRoot",
    unit: "SpineCrawlerUprooted",
  },
  zSpineCrawlerUproot: {
    name: "Uproot",
    category: "ability",
    key: "SpineCrawlerUproot",
    unit: "SpineCrawler",
  },
  zSporeCrawlerUproot: {
    name: "Uproot",
    category: "ability",
    key: "SporeCrawlerUproot",
    unit: "SporeCrawler",
  },
  zSporeCrawlerRootSporeCrawlerU: {
    name: "Root",
    category: "ability",
    key: "SporeCrawlerRoot",
    unit: "SporeCrawlerUprooted",
  },
  zEvolveGroovedSpinesHydraliskD: {
    name: "Grooved Spines",
    category: "ability",
    key: "EvolveGroovedSpines",
    unit: "HydraliskDen",
  },
  zEvolveMuscularAugmentsHydrali: {
    name: "Muscular Augments",
    category: "ability",
    key: "EvolveMuscularAugments",
    unit: "HydraliskDen",
  },
  zResearchFrenzyHydraliskDen: {
    name: "Nanomuscular Swell",
    category: "ability",
    key: "ResearchFrenzy",
    unit: "HydraliskDen",
  },
  zResearchNeuralParasiteInfesta: {
    name: "Neural Parasite",
    category: "ability",
    key: "ResearchNeuralParasite",
    unit: "InfestationPit",
  },
  zEvolveDiggingClawsLurkerDenMP: {
    name: "Adaptive Talons",
    category: "ability",
    key: "EvolveDiggingClaws",
    unit: "LurkerDenMP",
  },
  zResearchLurkerRangeLurkerDenM: {
    name: "Seismic Spines",
    category: "ability",
    key: "ResearchLurkerRange",
    unit: "LurkerDenMP",
  },
  zZergflyerattack1: { name: "Flyer Attacks", category: "ability", key: "zergflyerattack1" },
  zZergflyerarmor1: { name: "Flyer Carapace", category: "ability", key: "zergflyerarmor1" },
  zGreaterSpire: { name: "Greater Spire", category: "ability", key: "GreaterSpire", unit: "Spire" },
  zSummonNydusWormNydusNetwork: {
    name: "Nydus Worm",
    category: "ability",
    key: "SummonNydusWorm",
    unit: "NydusNetwork",
  },
  zEvolveChitinousPlatingUltrali: {
    name: "Chitinous Plating",
    category: "ability",
    key: "EvolveChitinousPlating",
    unit: "UltraliskCavern",
  },
  zEvolveAnabolicSynthesis2Ultra: {
    name: "Anabolic Synthesis",
    category: "ability",
    key: "EvolveAnabolicSynthesis2",
    unit: "UltraliskCavern",
  },
  pChargeZealot: { name: "Charge", category: "ability", key: "Charge", unit: "Zealot" },
  pForceFieldSentry: {
    name: "Force Field",
    category: "ability",
    key: "ForceField",
    unit: "Sentry",
  },
  pGuardianShieldSentry: {
    name: "Guardian Shield",
    category: "ability",
    key: "GuardianShield",
    unit: "Sentry",
  },
  pHallucinationSentry: {
    name: "Hallucination",
    category: "ability",
    key: "Hallucination",
    unit: "Sentry",
  },
  pProbeHallucinationSentry: {
    name: "Hal: Probe",
    category: "ability",
    key: "ProbeHallucination",
    unit: "Sentry",
  },
  pZealotHallucinationSentry: {
    name: "Hal: Zealot",
    category: "ability",
    key: "ZealotHallucination",
    unit: "Sentry",
  },
  pAdeptHallucinationSentry: {
    name: "Hal: Adept",
    category: "ability",
    key: "AdeptHallucination",
    unit: "Sentry",
  },
  pStalkerHallucinationSentry: {
    name: "Hal: Stalker",
    category: "ability",
    key: "StalkerHallucination",
    unit: "Sentry",
  },
  pImmortalHallucinationSentry: {
    name: "Hal: Immortal",
    category: "ability",
    key: "ImmortalHallucination",
    unit: "Sentry",
  },
  pHighTemplarHallucinationSentr: {
    name: "Hal: High Templar",
    category: "ability",
    key: "HighTemplarHallucination",
    unit: "Sentry",
  },
  pArchonHallucinationSentry: {
    name: "Hal: Archon",
    category: "ability",
    key: "ArchonHallucination",
    unit: "Sentry",
  },
  pVoidRayHallucinationSentry: {
    name: "Hal: Void Ray",
    category: "ability",
    key: "VoidRayHallucination",
    unit: "Sentry",
  },
  pPhoenixHallucinationSentry: {
    name: "Hal: Phoenix",
    category: "ability",
    key: "PhoenixHallucination",
    unit: "Sentry",
  },
  pWarpPrismHallucinationSentry: {
    name: "Hal: Warp Prism",
    category: "ability",
    key: "WarpPrismHallucination",
    unit: "Sentry",
  },
  pOracleHallucinationSentry: {
    name: "Hal: Oracle",
    category: "ability",
    key: "OracleHallucination",
    unit: "Sentry",
  },
  pColossusHallucinationSentry: {
    name: "Hal: Colossus",
    category: "ability",
    key: "ColossusHallucination",
    unit: "Sentry",
  },
  pDisruptorHallucinationSentry: {
    name: "Hal: Disruptor",
    category: "ability",
    key: "DisruptorHallucination",
    unit: "Sentry",
  },
  pAdeptPhaseShift: {
    name: "Psionic Transfer",
    category: "ability",
    key: "AdeptPhaseShift",
    unit: "Adept",
  },
  pFeedbackHighTemplar: {
    name: "Feedback",
    category: "ability",
    key: "Feedback",
    unit: "HighTemplar",
  },
  pPsiStormHighTemplar: {
    name: "Psionic Storm",
    category: "ability",
    key: "PsiStorm",
    unit: "HighTemplar",
  },
  pAWrp: { name: "Archon Warp", category: "ability", key: "AWrp" },
  pDarkTemplarBlink: {
    name: "Shadow Stride",
    category: "ability",
    key: "DarkTemplarBlink",
    unit: "DarkTemplar",
  },
  pMorphtoObserverSiege: {
    name: "Surveillance Mode",
    category: "ability",
    key: "MorphtoObserverSiege",
    unit: "Observer",
  },
  pMorphtoObserver: {
    name: "Observer Mode",
    category: "ability",
    key: "MorphtoObserver",
    unit: "Observer",
  },
  pPhasingModeWarpPrism: {
    name: "Phasing Mode",
    category: "ability",
    key: "PhasingMode",
    unit: "WarpPrism",
  },
  pTransportModeWarpPrism: {
    name: "Transport Mode",
    category: "ability",
    key: "TransportMode",
    unit: "WarpPrism",
  },
  pPurificationNovaTargetedDisru: {
    name: "Purification Nova",
    category: "ability",
    key: "PurificationNovaTargeted",
    unit: "Disruptor",
  },
  pGravitonBeamPhoenix: {
    name: "Graviton Beam",
    category: "ability",
    key: "GravitonBeam",
    unit: "Phoenix",
  },
  pOracleRevelation: {
    name: "Revelation",
    category: "ability",
    key: "OracleRevelation",
    unit: "Oracle",
  },
  pOracleBuildStasisTrap: {
    name: "Stasis Ward",
    category: "ability",
    key: "OracleBuildStasisTrap",
    unit: "Oracle",
  },
  pOracleWeaponOn: {
    name: "Activate Pulsar Beam",
    category: "ability",
    key: "OracleWeaponOn",
    unit: "Oracle",
  },
  pOracleWeaponOff: {
    name: "Deactivate Pulsar Beam",
    category: "ability",
    key: "OracleWeaponOff",
    unit: "Oracle",
  },
  pVoidRaySwarmDamageBoost: {
    name: "Prismatic Alignment",
    category: "ability",
    key: "VoidRaySwarmDamageBoost",
    unit: "VoidRay",
  },
  pInterceptorCarrier: {
    name: "Interceptor",
    category: "structure",
    key: "Interceptor",
    unit: "Carrier",
  },
  // Currency patch: the Mothership Core was removed from the live game (2016); its card
  // and commands are dropped. The Mothership is now trained at the Nexus (pMother) and keeps its own
  // context below. Source snapshot (jcfieldsdev) still listed the Core - see the decision's currency note.
  pMassRecallMothership: {
    name: "Mass Recall",
    category: "ability",
    key: "MassRecall",
    unit: "Mothership",
  },
  pTemporalFieldMothership: {
    name: "Time Warp",
    category: "ability",
    key: "TemporalField",
    unit: "Mothership",
  },
  // Currency patch (item 3): Cloaking Field became an activatable command-card ability in 5.0.12
  // (formerly a passive aura). TheCore binds CloakingField/Mothership.
  pCloakingFieldMothership: {
    name: "Cloaking Field",
    category: "ability",
    key: "CloakingField",
    unit: "Mothership",
  },
  pNexusMassRecall: {
    name: "Strategic Recall",
    category: "ability",
    key: "NexusMassRecall",
    unit: "Nexus",
  },
  pEnergyRechargeNexus: {
    name: "Energy Recharge",
    category: "ability",
    key: "EnergyRecharge",
    unit: "Nexus",
  },
  pWarpInAdeptGateway: { name: "Adept", category: "ability", key: "WarpInAdept", unit: "Gateway" },
  pUpgradeToWarpGateGateway: {
    name: "Transform to Warp Gate",
    category: "ability",
    key: "UpgradeToWarpGate",
    unit: "Gateway",
  },
  // Transform to Gateway sits on both structures: the Warp Gate's own morph, and - since the
  // 5.0.16.97563 hotfix put it at r2c1 on the Gateway card too - the Gateway's. Two entries because
  // the game scopes the button per caster, exactly like the WarpInAdept pair above.
  pMorphBackToGatewayGateway: {
    name: "Transform to Gateway",
    category: "ability",
    key: "MorphBackToGateway",
    unit: "Gateway",
  },
  pWarpInAdeptWarpGate: {
    name: "Adept",
    category: "ability",
    key: "WarpInAdept",
    unit: "WarpGate",
  },
  pMorphBackToGatewayWarpGate: {
    name: "Transform to Gateway",
    category: "ability",
    key: "MorphBackToGateway",
    unit: "WarpGate",
  },
  pShieldBatteryRecharge: {
    name: "Restore",
    category: "ability",
    key: "ShieldBatteryRecharge",
    unit: "ShieldBattery",
  },
  pProtossGroundWeaponsLevel1For: {
    name: "Ground Weapons",
    category: "command",
    key: "ProtossGroundWeaponsLevel1",
    unit: "Forge",
  },
  pProtossGroundArmorLevel1Forge: {
    name: "Ground Armor",
    category: "command",
    key: "ProtossGroundArmorLevel1",
    unit: "Forge",
  },
  pProtossShieldsLevel1Forge: {
    name: "Shields",
    category: "command",
    key: "ProtossShieldsLevel1",
    unit: "Forge",
  },
  pProtossAirWeaponsLevel1Cybern: {
    name: "Air Weapons",
    category: "command",
    key: "ProtossAirWeaponsLevel1",
    unit: "CyberneticsCore",
  },
  pProtossAirArmorLevel1Cybernet: {
    name: "Air Armor",
    category: "command",
    key: "ProtossAirArmorLevel1",
    unit: "CyberneticsCore",
  },
  pResearchWarpGateCyberneticsCo: {
    name: "Warp Gate",
    category: "command",
    key: "ResearchWarpGate",
    unit: "CyberneticsCore",
  },
  pResearchChargeTwilightCouncil: {
    name: "Charge",
    category: "command",
    key: "ResearchCharge",
    unit: "TwilightCouncil",
  },
  pResearchStalkerTeleportTwilig: {
    name: "Blink",
    category: "command",
    key: "ResearchStalkerTeleport",
    unit: "TwilightCouncil",
  },
  pAdeptResearchPiercingUpgradeT: {
    name: "Resonating Glaives",
    category: "command",
    key: "AdeptResearchPiercingUpgrade",
    unit: "TwilightCouncil",
  },
  pResearchPsiStormTemplarArchiv: {
    name: "Psionic Storm",
    category: "command",
    key: "ResearchPsiStorm",
    unit: "TemplarArchive",
  },
  pResearchDarkTemplarBlinkDarkS: {
    name: "Shadow Stride",
    category: "command",
    key: "ResearchDarkTemplarBlink",
    unit: "DarkShrine",
  },
  pObserverRoboticsFacility: {
    name: "Observer",
    category: "ability",
    key: "Observer",
    unit: "RoboticsFacility",
  },
  pWarpPrismRoboticsFacility: {
    name: "Warp Prism",
    category: "ability",
    key: "WarpPrism",
    unit: "RoboticsFacility",
  },
  pImmortalRoboticsFacility: {
    name: "Immortal",
    category: "ability",
    key: "Immortal",
    unit: "RoboticsFacility",
  },
  pColossusRoboticsFacility: {
    name: "Colossus",
    category: "ability",
    key: "Colossus",
    unit: "RoboticsFacility",
  },
  pWarpinDisruptorRoboticsFacili: {
    name: "Disruptor",
    category: "ability",
    key: "WarpinDisruptor",
    unit: "RoboticsFacility",
  },
  pResearchGraviticBoosterRoboti: {
    name: "Gravitic Boosters",
    category: "command",
    key: "ResearchGraviticBooster",
    unit: "RoboticsBay",
  },
  pResearchGraviticDriveRobotics: {
    name: "Gravitic Drive",
    category: "command",
    key: "ResearchGraviticDrive",
    unit: "RoboticsBay",
  },
  pResearchExtendedThermalLanceR: {
    name: "Extended Thermal Lance",
    category: "command",
    key: "ResearchExtendedThermalLance",
    unit: "RoboticsBay",
  },
  pPhoenixStargate: { name: "Phoenix", category: "ability", key: "Phoenix", unit: "Stargate" },
  pOracleStargate: { name: "Oracle", category: "ability", key: "Oracle", unit: "Stargate" },
  pVoidRayStargate: { name: "Void Ray", category: "ability", key: "VoidRay", unit: "Stargate" },
  pTempestStargate: { name: "Tempest", category: "ability", key: "Tempest", unit: "Stargate" },
  pCarrierStargate: { name: "Carrier", category: "ability", key: "Carrier", unit: "Stargate" },
  pAnionPulseCrystalsFleetBeacon: {
    name: "Anion Pulse-Crystals",
    category: "command",
    key: "AnionPulseCrystals",
    unit: "FleetBeacon",
  },
  pResearchVoidRaySpeedUpgradeFl: {
    name: "Flux Vanes",
    category: "command",
    key: "ResearchVoidRaySpeedUpgrade",
    unit: "FleetBeacon",
  },
  pTempestResearchGroundAttackUp: {
    name: "Tectonic Destabilizers",
    category: "command",
    key: "TempestResearchGroundAttackUpgrade",
    unit: "FleetBeacon",
  },
};

/** The full command registry: hand-authored multiplayer/campaign commands plus the generated co-op
 * commander commands. Co-op ids are prefixed `co_`; reuse keeps shared ids (bRax, tStim, ...) single. */
export const SC2_COMMANDS: Record<string, SC2Command> = {
  ...MP_COMMANDS,
  ...CAMPAIGN_COMMANDS,
  ...COOP_COMMANDS,
};

const MP_ROSTERS: Record<string, SC2Roster> = {
  terran: {
    label: "Terran",
    race: "terran",
    modes: ["multiplayer"],
    contexts: [
      { id: "none", label: "— nothing —", card: [] },
      {
        id: "scv",
        cluster: "basic",
        cancel: true,
        label: "SCV",
        kind: "unit",
        card: ["repair", "halt", "returnCargo", "spray", "bBasic", "bAdvanced"],
      },
      {
        id: "scvBasic",
        cancel: true,
        label: "Build Basic",
        kind: "building",
        parent: "scv",
        card: ["bCmdCtr", "bDepot", "bRax", "bRef", "bEbay", "bBunker", "bMissile", "bSensor"],
      },
      {
        id: "scvAdvanced",
        cancel: true,
        label: "Build Advanced",
        kind: "building",
        parent: "scv",
        card: ["bFactory", "bGhostAcad", "bArmory", "bStarport", "bFusion"],
      },
      {
        id: "mule",
        cluster: "flying",
        label: "MULE",
        kind: "unit",
        card: ["gatherProt", "returnCargo", "repair"],
      },
      { id: "marine", cluster: "basic", label: "Marine", kind: "unit", card: ["tStim"] },
      { id: "reaper", cluster: "basic", label: "Reaper", kind: "unit", card: ["tKD8ChargeReaper"] },
      { id: "marauder", cluster: "basic", label: "Marauder", kind: "unit", card: ["tStim"] },
      {
        id: "ghost",
        cluster: "basic",
        cancel: true,
        label: "Ghost",
        kind: "unit",
        card: [
          "nuke",
          "holdfire",
          "tWeaponsFreeGhost",
          "tChannelSnipeGhost",
          "emp",
          "tCloakOnBanshee",
          "tCloakOff",
        ],
      },
      {
        id: "hellbat",
        cluster: "basic",
        label: "Hellbat",
        kind: "unit",
        card: ["tMorphToHellion", "tMorphToHellionTank"],
      },
      {
        id: "widowMine",
        cluster: "flying",
        label: "Widow Mine",
        kind: "unit",
        card: ["tWidowMineBurrow", "tWidowMineUnburrow"],
      },
      { id: "cyclone", cluster: "basic", label: "Cyclone", kind: "unit", card: ["tLockOnCyclone"] },
      {
        id: "siegeTank",
        cluster: "basic",
        label: "Siege Tank",
        kind: "unit",
        card: ["tSiegeMode", "tUnsiege"],
      },
      {
        id: "thor",
        cluster: "basic",
        cancel: true,
        label: "Thor",
        kind: "unit",
        card: ["tArmorpiercingMode", "tExplosiveMode"],
      },
      {
        id: "viking",
        cluster: "basic",
        label: "Viking",
        kind: "unit",
        card: ["tAssaultMode", "tFighterMode"],
      },
      {
        id: "medivac",
        cluster: "flying",
        label: "Medivac",
        kind: "unit",
        card: ["tHealMedivac", "tMedivacSpeedBoost", "tBunkerLoad", "tBunkerUnloadAll"],
      },
      {
        id: "liberator",
        cluster: "basic",
        label: "Liberator",
        kind: "unit",
        card: ["tLiberatorAGMode", "tLiberatorAAMode"],
      },
      {
        id: "raven",
        cluster: "flying",
        label: "Raven",
        kind: "unit",
        card: ["tAutoTurretRaven", "tRavenScramblerMissile", "tRavenShredderMissile"],
      },
      {
        id: "banshee",
        cluster: "basic",
        label: "Banshee",
        kind: "unit",
        card: ["tCloakOnBanshee", "tCloakOff"],
      },
      {
        id: "battlecruiser",
        cluster: "basic",
        label: "Battlecruiser",
        kind: "unit",
        card: ["tYamatoGun", "tHyperjumpBattlecruiser"],
      },
      {
        id: "cc",
        cancel: true,
        label: "Command Center",
        kind: "building",
        card: [
          "tScv",
          "mOrbital",
          "tUpgradeToPlanetaryFortressCom",
          "tSelectBuilder",
          "tRally",
          "tCommandCenterLoad",
          "tCommandCenterUnloadAll",
          "lift",
        ],
      },
      {
        id: "commandCenterOrbitalCommand",
        cancel: true,
        label: "Command Center: Orbital Command",
        kind: "building",
        card: [
          "tScv",
          "tRally",
          "tCalldownMULEOrbitalCommand",
          "tSupplyDropOrbitalCommand",
          "tScanOrbitalCommand",
          "lift",
        ],
      },
      {
        id: "commandCenterPlanetaryFortress",
        cluster: "tower",
        cancel: true,
        label: "Command Center: Planetary Fortress",
        kind: "building",
        card: [
          "tScv",
          "tRally",
          "tStopPlanetaryFortress",
          "tCommandCenterLoad",
          "tCommandCenterUnloadAll",
        ],
      },
      {
        id: "refinery",
        cancel: true,
        label: "Refinery",
        kind: "building",
        card: ["tSelectBuilder", "tHalt"],
      },
      {
        id: "barracks",
        cancel: true,
        label: "Barracks",
        kind: "building",
        card: [
          "tMarine",
          "tReaper",
          "tMaraud",
          "tGhost",
          "tSelectBuilder",
          "tRally",
          "tTechLabBarracks",
          "aReactor",
          "lift",
        ],
      },
      {
        id: "barracksTechLab",
        cancel: true,
        label: "Barracks: Tech Lab",
        kind: "building",
        card: [
          "tResearchShieldWallBarracksTec",
          "tStimpackBarracksTechLab",
          "tResearchPunisherGrenadesBarra",
        ],
      },
      {
        id: "engineeringBay",
        cancel: true,
        label: "Engineering Bay",
        kind: "building",
        card: [
          "tTerranInfantryWeaponsLevel1En",
          "tTerranInfantryArmorLevel1Engi",
          "tResearchHiSecAutoTrackingEngi",
          "tUpgradeBuildingArmorLevel1Eng",
          "tSelectBuilder",
          "tHalt",
        ],
      },
      {
        id: "bunker",
        cancel: true,
        label: "Bunker",
        kind: "building",
        card: [
          "tSelectBuilder",
          "tRally",
          "tStim",
          "tBunkerLoad",
          "tBunkerUnloadAll",
          "tSalvageBunker",
        ],
      },
      {
        id: "missileTurret",
        cluster: "tower",
        cancel: true,
        label: "Missile Turret",
        kind: "building",
        card: ["tSelectBuilder", "tHalt"],
      },
      {
        id: "sensorTower",
        cancel: true,
        label: "Sensor Tower",
        kind: "building",
        card: ["tSelectBuilder", "tHalt"],
      },
      {
        id: "ghostAcademy",
        cancel: true,
        label: "Ghost Academy",
        kind: "building",
        card: ["tResearchPersonalCloakingGhost", "tNukeArmGhostAcademy", "tSelectBuilder", "tHalt"],
      },
      {
        id: "factory",
        cancel: true,
        label: "Factory",
        kind: "building",
        card: [
          "tHellionFactory",
          "tWidowMineFactory",
          "tBuildCycloneFactory",
          "tSiegeTankFactory",
          "tHellionTankFactory",
          "tThorFactory",
          "tSelectBuilder",
          "tRally",
          "tTechLabFactory",
          "tReactorFactory",
          "lift",
        ],
      },
      {
        id: "factoryTechLab",
        cancel: true,
        label: "Factory: Tech Lab",
        kind: "building",
        card: [
          "tResearchHighCapacityBarrelsFa",
          "tCycloneResearchLockOnDamageUp",
          "tResearchDrillClawsFactoryTech",
          "tResearchSmartServosFactoryTec",
        ],
      },
      {
        id: "armory",
        cancel: true,
        label: "Armory",
        kind: "building",
        card: [
          "tTerranVehicleWeaponsLevel1Arm",
          "tTerranVehicleAndShipPlatingLe",
          "tTerranShipWeaponsLevel1Armory",
          "tSelectBuilder",
          "tHalt",
        ],
      },
      {
        id: "starport",
        cancel: true,
        label: "Starport",
        kind: "building",
        card: [
          "tVikingFighterStarport",
          "tMedivacStarport",
          "tLiberatorStarport",
          "tRavenStarport",
          "tBansheeStarport",
          "tBattlecruiserStarport",
          "tSelectBuilder",
          "tRally",
          "tTechLabStarport",
          "tReactorStarport",
          "lift",
        ],
      },
      {
        id: "starportTechLab",
        cancel: true,
        label: "Starport: Tech Lab",
        kind: "building",
        card: [
          "tResearchBansheeCloakStarportT",
          "tBansheeSpeedStarportTechLab",
          "tResearchRavenInterferenceMatr",
        ],
      },
      {
        id: "fusionCore",
        cancel: true,
        label: "Fusion Core",
        kind: "building",
        card: [
          "tResearchBattlecruiserSpeciali",
          "tResearchMedivacEnergyUpgradeF",
          "tResearchBallisticRangeFusionC",
          "tSelectBuilder",
          "tHalt",
        ],
      },
    ],
  },
  zerg: {
    label: "Zerg",
    race: "zerg",
    modes: ["multiplayer"],
    contexts: [
      { id: "none", label: "— nothing —", card: [] },
      {
        id: "drone",
        cluster: "basic",
        cancel: true,
        label: "Drone",
        kind: "unit",
        card: ["burrow", "unburrow", "returnCargo", "spray", "zBuild", "zBuildAdv"],
      },
      {
        id: "droneBasic",
        cancel: true,
        label: "Build Basic",
        kind: "building",
        parent: "drone",
        card: [
          "zbHatch",
          "zbExtract",
          "zbPool",
          "zbEvo",
          "zbRoachW",
          "zbBane",
          "zbSpine",
          "zbSpore",
        ],
      },
      {
        id: "droneAdvanced",
        cancel: true,
        label: "Build Advanced",
        kind: "building",
        parent: "drone",
        card: ["zbHydra", "zbInfPit", "zbSpire", "zbNydus", "zbUltra", "zbLurker"],
      },
      {
        id: "larva",
        cancel: true,
        label: "Larva",
        kind: "unit",
        card: [
          "zDrone",
          "zOver",
          "zZerglingLarva",
          "zRoach",
          "zHydra",
          "zMuta",
          "zCorrupt",
          "zInfest",
          "zSwarmHostMPLarva",
          "zViperLarva",
          "zUltraliskLarva",
        ],
      },
      { id: "larvaCocoon", cancel: true, label: "Larva: Cocoon", kind: "unit", card: ["tRally"] },
      {
        id: "queen",
        cluster: "basic",
        label: "Queen",
        kind: "unit",
        card: ["qCreep", "zMorphMorphaliskQueen", "zTransfusionQueen", "burrow", "unburrow"],
      },
      {
        id: "queenCreepTumor",
        cluster: "basic",
        label: "Queen: Creep Tumor",
        kind: "unit",
        card: ["zBuildCreepTumorPropagateCreep"],
      },
      {
        id: "overlord",
        cluster: "flying",
        cancel: true,
        label: "Overlord",
        kind: "unit",
        card: ["zMorphToOverseerOverlord", "zGenerateCreepOverlord", "zMorphtoOverlordTransport"],
      },
      {
        id: "overlordVentralSacs",
        cluster: "flying",
        cancel: true,
        label: "Overlord: Ventral Sacs",
        kind: "unit",
        card: [
          "zMorphToOverseerOverlord",
          "zGenerateCreepOverlord",
          "tBunkerLoad",
          "tBunkerUnloadAll",
        ],
      },
      {
        id: "overlordGeneratingCreep",
        cluster: "flying",
        cancel: true,
        label: "Overlord: Generating Creep",
        kind: "unit",
        card: ["zStopGenerateCreep"],
      },
      {
        id: "overseer",
        cluster: "flying",
        label: "Overseer",
        kind: "unit",
        card: [
          "zMorphtoOverseerSiege",
          "zMorphtoOverseerNormal",
          "zSpawnChangelingOverseer",
          "zContaminateOverseer",
        ],
      },
      {
        id: "zergling",
        cluster: "basic",
        label: "Zergling",
        kind: "unit",
        card: ["zBanelingZergling", "burrow", "unburrow"],
      },
      {
        id: "baneling",
        cluster: "basic",
        label: "Baneling",
        kind: "unit",
        card: [
          "zExplodeBaneling",
          "zEnableBuildingAttackBaneling",
          "zDisableBuildingAttackBaneling",
          "burrow",
        ],
      },
      {
        id: "roach",
        cluster: "basic",
        label: "Roach",
        kind: "unit",
        card: ["zRavagerRoach", "burrow", "unburrow"],
      },
      {
        id: "ravager",
        cluster: "basic",
        label: "Ravager",
        kind: "unit",
        card: ["zRavagerCorrosiveBile", "burrow", "unburrow"],
      },
      {
        id: "hydralisk",
        cluster: "basic",
        label: "Hydralisk",
        kind: "unit",
        card: ["zHydraliskFrenzy", "zLurkerMPHydralisk", "burrow", "unburrow"],
      },
      { id: "lurker", cluster: "flying", label: "Lurker", kind: "unit", card: ["zBurrowLurkerMP"] },
      {
        id: "corruptor",
        cluster: "basic",
        cancel: true,
        label: "Corruptor",
        kind: "unit",
        card: ["zCausticSprayCorruptor", "zBroodLordCorruptor"],
      },
      {
        id: "infestor",
        cluster: "flying",
        cancel: true,
        label: "Infestor",
        kind: "unit",
        card: [
          "zAmorphousArmorcloudInfestor",
          "zFungalGrowthInfestor",
          "zNeuralParasiteInfestor",
          "burrow",
        ],
      },
      {
        id: "swarmHost",
        cluster: "flying",
        label: "Swarm Host",
        kind: "unit",
        card: ["zVoidSwarmHostSpawnLocustSwarm", "zSwarmHostBurrowDown"],
      },
      {
        id: "swarmHostLocust",
        cluster: "flying",
        label: "Swarm Host: Locust",
        kind: "unit",
        card: ["zLocustMPFlyingSwoop"],
      },
      {
        id: "viper",
        cluster: "flying",
        label: "Viper",
        kind: "unit",
        card: ["zViperConsume", "zFaceEmbraceViper", "zBlindingCloudViper", "zParasiticBombViper"],
      },
      {
        id: "ultralisk",
        cluster: "basic",
        label: "Ultralisk",
        kind: "unit",
        card: ["burrow", "unburrow"],
      },
      {
        id: "hatchery",
        cancel: true,
        label: "Hatchery",
        kind: "building",
        card: [
          "zLarva",
          "zQueen",
          "zResearchBurrow",
          "zOverlordspeed",
          "zRallyEgg",
          "tRally",
          "zLairHatchery",
        ],
      },
      {
        id: "hatcheryLair",
        cancel: true,
        label: "Hatchery: Lair",
        kind: "building",
        card: [
          "zLarva",
          "zQueen",
          "zResearchBurrow",
          "zOverlordspeed",
          "zRallyEgg",
          "tRally",
          "zHiveLair",
        ],
      },
      {
        id: "hatcheryHive",
        cancel: true,
        label: "Hatchery: Hive",
        kind: "building",
        card: ["zLarva", "zQueen", "zResearchBurrow", "zOverlordspeed", "zRallyEgg", "tRally"],
      },
      {
        id: "spawningPool",
        cancel: true,
        label: "Spawning Pool",
        kind: "building",
        card: ["zZerglingmovementspeedSpawning", "zZerglingattackspeedSpawningPo"],
      },
      {
        id: "evolutionChamber",
        cancel: true,
        label: "Evolution Chamber",
        kind: "building",
        card: [
          "zZergmeleeweapons1EvolutionCha",
          "zZergmissileweapons1EvolutionC",
          "zZerggroundarmor1EvolutionCham",
        ],
      },
      {
        id: "roachWarren",
        cancel: true,
        label: "Roach Warren",
        kind: "building",
        card: ["zEvolveGlialRegenerationRoachW", "zEvolveTunnelingClawsRoachWarr"],
      },
      {
        id: "banelingNest",
        cancel: true,
        label: "Baneling Nest",
        kind: "building",
        card: ["zEvolveCentrificalHooksBanelin"],
      },
      {
        id: "spineCrawler",
        cluster: "tower",
        cancel: true,
        label: "Spine Crawler",
        kind: "building",
        card: ["zSpineCrawlerRootSpineCrawlerU", "zSpineCrawlerUproot"],
      },
      {
        id: "sporeCrawler",
        cluster: "tower",
        cancel: true,
        label: "Spore Crawler",
        kind: "building",
        card: ["zSporeCrawlerUproot", "zSporeCrawlerRootSporeCrawlerU"],
      },
      {
        id: "hydraliskDen",
        cancel: true,
        label: "Hydralisk Den",
        kind: "building",
        card: [
          "zEvolveGroovedSpinesHydraliskD",
          "zEvolveMuscularAugmentsHydrali",
          "zResearchFrenzyHydraliskDen",
        ],
      },
      {
        id: "infestationPit",
        cancel: true,
        label: "Infestation Pit",
        kind: "building",
        card: ["zResearchNeuralParasiteInfesta"],
      },
      {
        id: "lurkerDen",
        cancel: true,
        label: "Lurker Den",
        kind: "building",
        card: ["zEvolveDiggingClawsLurkerDenMP", "zResearchLurkerRangeLurkerDenM"],
      },
      {
        id: "spire",
        cancel: true,
        label: "Spire",
        kind: "building",
        card: ["zZergflyerattack1", "zZergflyerarmor1", "zGreaterSpire"],
      },
      {
        id: "spireGreaterSpire",
        cancel: true,
        label: "Spire: Greater Spire",
        kind: "building",
        card: ["zZergflyerattack1", "zZergflyerarmor1"],
      },
      {
        id: "nydusNetwork",
        cancel: true,
        label: "Nydus Network",
        kind: "building",
        card: ["zSummonNydusWormNydusNetwork", "tRally", "tBunkerLoad", "tBunkerUnloadAll"],
      },
      {
        id: "nydusNetworkNydusWorm",
        cancel: true,
        label: "Nydus Network: Nydus Worm",
        kind: "building",
        card: ["tRally", "tBunkerLoad", "tBunkerUnloadAll"],
      },
      {
        id: "ultraliskCavern",
        cancel: true,
        label: "Ultralisk Cavern",
        kind: "building",
        card: ["zEvolveChitinousPlatingUltrali", "zEvolveAnabolicSynthesis2Ultra"],
      },
    ],
  },
  protoss: {
    label: "Protoss",
    race: "protoss",
    modes: ["multiplayer"],
    contexts: [
      { id: "none", label: "— nothing —", card: [] },
      {
        id: "probe",
        cluster: "basic",
        cancel: true,
        label: "Probe",
        kind: "unit",
        card: ["gatherProt", "returnCargo", "spray", "pBuild", "pBuildAdv"],
      },
      {
        id: "probeBasic",
        cancel: true,
        label: "Build Basic",
        kind: "building",
        parent: "probe",
        card: ["pNexus", "pPylon", "pAssim", "pGate", "pForge", "pCyber", "pCannon", "pBattery"],
      },
      {
        id: "probeAdvanced",
        cancel: true,
        label: "Build Advanced",
        kind: "building",
        parent: "probe",
        card: ["pTwilight", "pStargate", "pRobo", "pArchives", "pDark", "pFleet", "pRoboBay"],
      },
      { id: "zealot", cluster: "basic", label: "Zealot", kind: "unit", card: ["pChargeZealot"] },
      {
        id: "sentry",
        cluster: "basic",
        cancel: true,
        label: "Sentry",
        kind: "unit",
        card: ["pForceFieldSentry", "pGuardianShieldSentry", "pHallucinationSentry"],
      },
      {
        id: "sentryHallucinations",
        cluster: "basic",
        cancel: true,
        label: "Sentry: Hallucinations",
        kind: "unit",
        card: [
          "pProbeHallucinationSentry",
          "pZealotHallucinationSentry",
          "pAdeptHallucinationSentry",
          "pStalkerHallucinationSentry",
          "pImmortalHallucinationSentry",
          "pHighTemplarHallucinationSentr",
          "pArchonHallucinationSentry",
          "pVoidRayHallucinationSentry",
          "pPhoenixHallucinationSentry",
          "pWarpPrismHallucinationSentry",
          "pOracleHallucinationSentry",
          "pColossusHallucinationSentry",
          "pDisruptorHallucinationSentry",
        ],
      },
      { id: "stalker", cluster: "basic", label: "Stalker", kind: "unit", card: ["pBlink"] },
      {
        id: "adept",
        cluster: "basic",
        cancel: true,
        label: "Adept",
        kind: "unit",
        card: ["pAdeptPhaseShift"],
      },
      {
        id: "highTemplar",
        cluster: "basic",
        label: "High Templar",
        kind: "unit",
        card: ["pFeedbackHighTemplar", "pPsiStormHighTemplar", "pAWrp"],
      },
      {
        id: "darkTemplar",
        cluster: "basic",
        label: "Dark Templar",
        kind: "unit",
        card: ["pDarkTemplarBlink", "pAWrp"],
      },
      {
        id: "observer",
        cluster: "flying",
        label: "Observer",
        kind: "unit",
        card: ["pMorphtoObserverSiege", "pMorphtoObserver"],
      },
      {
        id: "warpPrism",
        cluster: "flying",
        label: "Warp Prism",
        kind: "unit",
        card: [
          "pPhasingModeWarpPrism",
          "pTransportModeWarpPrism",
          "tBunkerLoad",
          "tBunkerUnloadAll",
        ],
      },
      {
        id: "disruptor",
        cluster: "flying",
        label: "Disruptor",
        kind: "unit",
        card: ["pPurificationNovaTargetedDisru"],
      },
      {
        id: "phoenix",
        cluster: "basic",
        cancel: true,
        label: "Phoenix",
        kind: "unit",
        card: ["pGravitonBeamPhoenix"],
      },
      {
        id: "oracle",
        cluster: "basic",
        cancel: true,
        label: "Oracle",
        kind: "unit",
        card: [
          "pOracleRevelation",
          "pOracleBuildStasisTrap",
          "pOracleWeaponOn",
          "pOracleWeaponOff",
        ],
      },
      {
        id: "voidRay",
        cluster: "basic",
        cancel: true,
        label: "Void Ray",
        kind: "unit",
        card: ["pVoidRaySwarmDamageBoost"],
      },
      {
        id: "carrier",
        cluster: "basic",
        cancel: true,
        label: "Carrier",
        kind: "unit",
        card: ["pInterceptorCarrier"],
      },
      {
        id: "mothership",
        cluster: "basic",
        label: "Mothership",
        kind: "unit",
        card: ["pMassRecallMothership", "pTemporalFieldMothership", "pCloakingFieldMothership"],
      },
      {
        id: "nexus",
        cancel: true,
        label: "Nexus",
        kind: "building",
        card: [
          "pProbe",
          "pMother",
          "tRally",
          "pChrono",
          "pNexusMassRecall",
          "pEnergyRechargeNexus",
        ],
      },
      {
        id: "gateway",
        cancel: true,
        label: "Gateway",
        kind: "building",
        card: [
          "pZealot",
          "pSentry",
          "pStalker",
          "pWarpInAdeptGateway",
          "pHT",
          "pDT",
          "tRally",
          "pUpgradeToWarpGateGateway",
          "pMorphBackToGatewayGateway",
        ],
      },
      {
        id: "gatewayWarpGate",
        cancel: true,
        label: "Gateway: Warp Gate",
        kind: "building",
        card: [
          "pZealot",
          "pSentry",
          "pStalker",
          "pWarpInAdeptWarpGate",
          "pHT",
          "pDT",
          "pMorphBackToGatewayWarpGate",
        ],
      },
      {
        id: "shieldBattery",
        cancel: true,
        label: "Shield Battery",
        kind: "building",
        card: ["pShieldBatteryRecharge"],
      },
      {
        id: "forge",
        cancel: true,
        label: "Forge",
        kind: "building",
        card: [
          "pProtossGroundWeaponsLevel1For",
          "pProtossGroundArmorLevel1Forge",
          "pProtossShieldsLevel1Forge",
        ],
      },
      {
        id: "cyberneticsCore",
        cancel: true,
        label: "Cybernetics Core",
        kind: "building",
        card: [
          "pProtossAirWeaponsLevel1Cybern",
          "pProtossAirArmorLevel1Cybernet",
          "pResearchWarpGateCyberneticsCo",
        ],
      },
      {
        id: "twilightCouncil",
        cancel: true,
        label: "Twilight Council",
        kind: "building",
        card: [
          "pResearchChargeTwilightCouncil",
          "pResearchStalkerTeleportTwilig",
          "pAdeptResearchPiercingUpgradeT",
        ],
      },
      {
        id: "templarArchives",
        cancel: true,
        label: "Templar Archives",
        kind: "building",
        card: ["pResearchPsiStormTemplarArchiv"],
      },
      {
        id: "darkShrine",
        cancel: true,
        label: "Dark Shrine",
        kind: "building",
        card: ["pResearchDarkTemplarBlinkDarkS"],
      },
      {
        id: "roboticsFacility",
        cancel: true,
        label: "Robotics Facility",
        kind: "building",
        card: [
          "pObserverRoboticsFacility",
          "pWarpPrismRoboticsFacility",
          "pImmortalRoboticsFacility",
          "pColossusRoboticsFacility",
          "pWarpinDisruptorRoboticsFacili",
          "tRally",
        ],
      },
      {
        id: "roboticsBay",
        cancel: true,
        label: "Robotics Bay",
        kind: "building",
        card: [
          "pResearchGraviticBoosterRoboti",
          "pResearchGraviticDriveRobotics",
          "pResearchExtendedThermalLanceR",
        ],
      },
      {
        id: "stargate",
        cancel: true,
        label: "Stargate",
        kind: "building",
        card: [
          "pPhoenixStargate",
          "pOracleStargate",
          "pVoidRayStargate",
          "pTempestStargate",
          "pCarrierStargate",
          "tRally",
        ],
      },
      {
        id: "fleetBeacon",
        cancel: true,
        label: "Fleet Beacon",
        kind: "building",
        card: [
          "pAnionPulseCrystalsFleetBeacon",
          "pResearchVoidRaySpeedUpgradeFl",
          "pTempestResearchGroundAttackUp",
        ],
      },
    ],
  },
};

/**
 * Every selectable SC2 roster: the hand-authored MP/campaign races plus the 18 generated co-op
 * commanders (sc2-coop.ts). Keyed by roster id (`terran` / `raynor` / ...); a co-op key never
 * collides with a race key, so the `${roster}/${context}` id space stays unique.
 */
export const SC2_ROSTERS: Record<string, SC2Roster> = {
  ...MP_ROSTERS,
  ...CAMPAIGN_ROSTERS,
  ...COOP_ROSTERS,
};
